import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Eye, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type PolicyStatus = 'sanctioned' | 'unsanctioned' | 'blocked' | 'under_review';
type ToolCategory = 'llm' | 'code' | 'image' | 'voice' | 'data' | 'other';

interface ShadowAITool {
  id: string;
  name: string;
  category: ToolCategory;
  users_count: number;
  risk_level: RiskLevel;
  policy_status: PolicyStatus;
  first_detected: string;
  last_seen: string;
}

interface ShadowAIResponse {
  tools: ShadowAITool[];
  stats: {
    detected: number;
    sanctioned: number;
    unsanctioned: number;
    high_critical_risk: number;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function riskBadge(level: RiskLevel) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (level) {
    case 'critical':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Critical</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Low</span>;
  }
}

function policyBadge(status: PolicyStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'sanctioned':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Sanctioned</span>;
    case 'unsanctioned':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Unsanctioned</span>;
    case 'blocked':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Blocked</span>;
    case 'under_review':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Under Review</span>;
  }
}

function categoryBadge(cat: ToolCategory) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  const colors: Record<ToolCategory, string> = {
    llm: 'bg-purple-500/15 text-purple-500',
    code: 'bg-blue-500/15 text-blue-500',
    image: 'bg-pink-500/15 text-pink-500',
    voice: 'bg-cyan-500/15 text-cyan-500',
    data: 'bg-emerald-500/15 text-emerald-500',
    other: 'bg-muted text-muted-foreground',
  };
  const labels: Record<ToolCategory, string> = {
    llm: 'LLM',
    code: 'Code',
    image: 'Image',
    voice: 'Voice',
    data: 'Data',
    other: 'Other',
  };
  return <span className={`${base} ${colors[cat]}`}>{labels[cat]}</span>;
}

const CATEGORY_TABS: { value: ToolCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'llm', label: 'LLM' },
  { value: 'code', label: 'Code' },
  { value: 'image', label: 'Image' },
  { value: 'voice', label: 'Voice' },
  { value: 'data', label: 'Data' },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ShadowAIPage() {
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<ToolCategory | 'all'>('all');

  const shadowQuery = useQuery<ShadowAIResponse>({
    queryKey: ['governance', 'shadow-ai'],
    queryFn: () => api<ShadowAIResponse>('governance/shadow-ai'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, policy_status }: { id: string; policy_status: PolicyStatus }) =>
      api('governance/shadow-ai', { method: 'PUT', body: { id, policy_status } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'shadow-ai'] });
    },
  });

  const stats = shadowQuery.data?.stats;
  const tools = shadowQuery.data?.tools ?? [];
  const filtered = categoryFilter === 'all' ? tools : tools.filter((t) => t.category === categoryFilter);

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Eye className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Shadow AI Governance</h1>
          <p className="text-muted-foreground mt-1">
            Detect, monitor, and govern unsanctioned AI tool usage across your organization.
          </p>
        </div>
      </div>

      {/* Stats */}
      {shadowQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Detected Tools</p>
            <p className="text-2xl font-bold text-foreground">{stats?.detected ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Sanctioned</p>
            <p className="text-2xl font-bold text-green-500">{stats?.sanctioned ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Unsanctioned</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.unsanctioned ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">High / Critical Risk</p>
            <p className="text-2xl font-bold text-red-500">{stats?.high_critical_risk ?? 0}</p>
          </div>
        </div>
      )}

      {/* Category filter tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setCategoryFilter(tab.value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              categoryFilter === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tools table */}
      {shadowQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Scanning for shadow AI tools...</span>
        </div>
      )}

      {shadowQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          {categoryFilter === 'all'
            ? 'No shadow AI tools detected yet.'
            : `No tools in the "${categoryFilter}" category.`}
        </div>
      )}

      {shadowQuery.isSuccess && filtered.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Name</span>
            <span className="w-16">Category</span>
            <span className="w-16 text-right">Users</span>
            <span className="w-20">Risk</span>
            <span className="w-24">Policy</span>
            <span className="w-44">Actions</span>
          </div>
          {filtered.map((tool) => (
            <div
              key={tool.id}
              className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50"
            >
              <span className="flex-1 text-sm font-medium text-foreground truncate">{tool.name}</span>
              <span className="w-16">{categoryBadge(tool.category)}</span>
              <span className="w-16 text-sm text-muted-foreground text-right">{tool.users_count}</span>
              <span className="w-20">{riskBadge(tool.risk_level)}</span>
              <span className="w-24">{policyBadge(tool.policy_status)}</span>
              <span className="w-44 flex items-center gap-2">
                {tool.policy_status !== 'sanctioned' && (
                  <button
                    type="button"
                    disabled={updateMutation.isPending}
                    onClick={() => updateMutation.mutate({ id: tool.id, policy_status: 'sanctioned' })}
                    className="px-3 py-1 rounded-md text-xs font-medium bg-green-500/15 text-green-500 hover:bg-green-500/25 transition-colors disabled:opacity-50"
                  >
                    Sanction
                  </button>
                )}
                {tool.policy_status !== 'blocked' && (
                  <button
                    type="button"
                    disabled={updateMutation.isPending}
                    onClick={() => updateMutation.mutate({ id: tool.id, policy_status: 'blocked' })}
                    className="px-3 py-1 rounded-md text-xs font-medium bg-red-500/15 text-red-500 hover:bg-red-500/25 transition-colors disabled:opacity-50"
                  >
                    Block
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Policy summary */}
      {shadowQuery.isSuccess && tools.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Policy Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-medium text-foreground mb-1">Sanctioned Tools</h3>
              <p className="text-sm text-muted-foreground">
                {tools.filter((t) => t.policy_status === 'sanctioned').length} tools approved for use.
                These are verified and compliant with organizational policies.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-medium text-foreground mb-1">Under Review</h3>
              <p className="text-sm text-muted-foreground">
                {tools.filter((t) => t.policy_status === 'under_review').length} tools pending review.
                These need evaluation before approval or blocking.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-medium text-foreground mb-1">Blocked Tools</h3>
              <p className="text-sm text-muted-foreground">
                {tools.filter((t) => t.policy_status === 'blocked').length} tools blocked.
                Usage of these tools violates organizational policy.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
